<script lang="ts">
  import FaPlusSquare from "svelte-icons/fa/FaPlusSquare.svelte";
  import FaBars from 'svelte-icons/fa/FaBars.svelte';
  import FaSortDown from "svelte-icons/fa/FaSortDown.svelte";
  import FaSignOutAlt from 'svelte-icons/fa/FaSignOutAlt.svelte'
  import IoMdCheckmark from 'svelte-icons/io/IoMdCheckmark.svelte';
  import MdLanguage from 'svelte-icons/md/MdLanguage.svelte';
  import FaDatabase from 'svelte-icons/fa/FaDatabase.svelte'
  import IoMdClose from 'svelte-icons/io/IoMdClose.svelte';
  import FaTimes from 'svelte-icons/fa/FaTimes.svelte'
  import FaLinkedin from 'svelte-icons/fa/FaLinkedin.svelte'
  import FaTwitter from 'svelte-icons/fa/FaTwitter.svelte'
  import FaFacebookF from 'svelte-icons/fa/FaFacebookF.svelte'
  import visitoryLogoUrl from "./images/logo-visitory.svg";
  import visitoryBlackLogoUrl from "./images/logo-visitory-black.svg";
  import { TimeSeriesChart } from "./data-visualization/charts/highcharts-time-series";
  import { VisitorySource } from "./data-sources/visitory-source";
  import TableView from "./data-views/table/table-view.svelte";
  import ChartView from "./data-views/chart/chart-view.svelte";
  import FigureView from "./data-views/figure/figure-view.svelte";
  import { visualizationDataStore, showHeaderStore } from "./data-views/stores";
  import { Languages, Visualizations, languageMapping } from "./data-views/options";
  import IconDropdown from "./ui-components/icon-dropdown.svelte";
  import ViewsDropdown from "./ui-components/views-dropdown.svelte";
  import VisualizationSelector from "./ui-components/visualization-selector.svelte";
  import ToggleSwitch from "./ui-components/toggle-switch.svelte";
  import FeedbackDrawer from "./ui-components/feedback-drawer.svelte";
  import FloatingButton from "./ui-components/floating-button.svelte";
  import { horizontalSlide } from './ui-components/transitions';
  import { onMount, tick } from "svelte";
  import { fade } from 'svelte/transition';
  import { t, locale } from "./translations/i18n";

  const visitorySource = new VisitorySource();

  // Store the visualization data in the store so the data is not lost when the page is reloaded
  let visualizationDataCollection: any[] = [];

  let resetConfirmation = false;

  let sideBarOpen = false;

  // Variables for controlling the sublists in the side menu
  let showSublist = {
    tourismresearch: false,
    help: false
  };

  // Variable for controlling the presentation mode (hide visualization headers)
  let hideVisualizationHeaders = false;
  $: handlePresentationMode(hideVisualizationHeaders);

  // Variables for controlling the focus on the elements for keyboard navigation
  let focusedFooterElementIndex = 0;
  let navbarFocused = false;
  let sideMenuFocused = false;
  let footerFocused = false;

  // Variable for controlling the feedback drawer (bottom right corner)
  let showFeedbackDrawer = false;

  function handleToggleDrawer() {
    showFeedbackDrawer = !showFeedbackDrawer;
  }

  // Subscribe to the store to get the visualization data
  visualizationDataStore.subscribe((data) => {
    visualizationDataCollection = data;
  });

  /**
   * Function for adding a new visualization to the page
   * 
   * @param visualization - The visualization type (right now only chart, table, figure)
  */
  const addVisualizationHandler: any = (visualization: string) => {
    // Visualization selector which is shown when there are no visualizations
    let visualization_selector = document.querySelector(".visualization-selector");

    // Set the display to none
    if (visualization_selector) {
      (visualization_selector as HTMLElement).style.display= "none";
    }

    // Get the container for the visualizations
    const target = document.querySelector("main .visualizations");
    if (!target) throw new Error("Container for visualizations not found");

    switch (visualization) {
      case Visualizations.CHART:
        new ChartView({
          target,
          props: {
            chart: new TimeSeriesChart(),
            dataSource: visitorySource,
            showSelectorButtons: false,
            filterSelectProps: [],
          },
        });
        break;
      case Visualizations.TABLE:
        new TableView({
          target,
          props: {
            dataSource: visitorySource,
            showSelectorButtons: false,
          },
        });
        break;
      case Visualizations.FIGURE:
        new FigureView({
          target,
          props: {
            dataSource: visitorySource,
            showSelectorButtons: false,
          },
        });
        break;
    }
  };

  /**
   * Function for removing all visualizations and reloading the page
   */
  const confirmReset = () => {
    visualizationDataStore.resetStore();
    location.reload();
  };

  const cancelReset = () => {
    resetConfirmation = false;
  };

  /**
   * Function for creating the visualization selector when there are no visualizations
   */
   const createVisualizationSelector = () => {
    // Get the container for the visualizations
    const target = document.querySelector("main .visualizations");
    if (!target) throw new Error("Container for visualizations not found");

    // Create VisualizationSelector when there are no visualizations
    new VisualizationSelector({
      target,
      props: {
        handler: addVisualizationHandler,
      }
    });
  };

  /**
   * Function for restoring the previous visualizations from the visualizationDataCollection (local storage)
   */
  const restorePreviousVisualizations = () => {
    const target = document.querySelector("main .visualizations");
    if (!target) throw new Error("Container for visualizations not found");

    // Loop through the visualization data collection and create the visualizations
    for (let i = 0; i < visualizationDataCollection.length; i++) {
        const visualization = visualizationDataCollection[i];
        switch (visualization.type) {
          case Visualizations.CHART:
            new ChartView({
              target,
              props: {
                chart: new TimeSeriesChart(),
                dataSource: visitorySource,
                showSelectorButtons: false,
                filterSelectProps: [],
                restoreView: true,
                storageData: visualization,
              },
            });
            break;
          case Visualizations.TABLE:
            new TableView({
              target,
              props: {
                dataSource: visitorySource,
                showSelectorButtons: false,
                restoreView: true,
                storageData: visualization,
              },
            });
            break;
          case Visualizations.FIGURE:
            new FigureView({
              target,
              props: {
                dataSource: visitorySource,
                showSelectorButtons: false,
                restoreView: true,
                storageData: visualization,
              },
            });
            break;
        }
      }
  };

  /**
   * Function for loading the view from the event
   * 
   * @param event - The event with the view data
   */
  const loadView = (event: any) => {
    // Get the view data from the event and overwrite the local storage
    if (!event.detail["view"]) return;
    visualizationDataCollection = event.detail["view"];
    // Set the new data to the store
    visualizationDataStore.set(visualizationDataCollection);
    location.reload();
  }


  /**
   * Function for changing the language
   * @param language - 
   */
  const changeLanguageHandler = async (language: any) => {

    // Ensure we're working with a single string
    const selectedLanguage = Array.isArray(language) ? language[0] : language;

    // Get the language code from the language mapping
    const locale_language = languageMapping[selectedLanguage];

    if (locale_language) {
      // Set the language cookie
      document.cookie = `visitory_language=${locale_language.split('-')[0]}; path=/; max-age=Session; SameSite=None; Secure;`;
    }
    locale.set(locale_language);

    // Redirect to the link with the new language
    window.location.href = document.URL.replace(/(en|fi|sv|nb)/, locale_language.split('-')[0]);
  };

  /** 
   * Function for opening the side menu
   */
  const openSideMenu = () => {
    sideBarOpen = true;
    sideMenuFocused = true;
    const mainView = document.getElementsByTagName("main")[0];
    mainView.style.overflow = "hidden"
    mainView.style.height = "100vh";
  };

  /**
   * Function for closing the side menu
   */
  const closeSideMenu = () => {
    sideBarOpen = false;
    navbarFocused = true;
    sideMenuFocused = false;
    showSublist = {
      tourismresearch: false,
      help: false
    };

    const mainView = document.getElementsByTagName("main")[0];
    mainView.style.removeProperty("overflow");
    mainView.style.height = "auto";
  };

  /**
   * Function for toggling the sublist in the side menu
   * 
   * @param tag_id - The tag id of the sublist
   */
  const toggleSublist = (tag_id: keyof typeof showSublist) => {
    return (event: any) => {
      event.preventDefault();
      showSublist = {
        ...showSublist,
        [tag_id]: !showSublist[tag_id]
      };
    };
  };

  /**
   * Function for handling the presentation mode (hide/show visualization headers)
   * 
   * @param switchState - The state of the switch
   */
  const handlePresentationMode = (switchState: boolean) => {
    if (switchState) {
      // Hide headers
      showHeaderStore.set(false);
    } else {
      // Show headers
      showHeaderStore.set(true);
    }
  }

  onMount(async () => {
    if (visualizationDataCollection.length === 0) {
      createVisualizationSelector();
    } else {
      restorePreviousVisualizations();
    }
  });

  /**
   * Handle keyboard events
   * @param event - The keyboard event
   * @returns void
  */
  const onKeyDown = async (event: KeyboardEvent) => {
    // Footer: get all elements
    let footerElements = document.querySelectorAll('#footer-content li a, #footer-icons a');
    focusedFooterElementIndex = Array.from(footerElements).findIndex((element: Element) => 
      element === document.activeElement || element.contains(document.activeElement)
    );

    // Navbar: Get all elements
    let navbarElements = [
      ...Array.from(document.querySelectorAll('[id^="navbar-"]'))
        .map((element: Element) => element.firstElementChild)
        .filter((element): element is Element => element !== null),
      ...(document.getElementById("app-logout")?.firstChild ? [document.getElementById("app-logout")] : [])
    ];

    // Find the index of the currently focused element from navbarElements
    let focusedElementIndex = navbarElements.findIndex((element: Element | null) => 
      element === document.activeElement || (element && element.contains(document.activeElement))
    );

    const isNavbarFocused = (element: any) => {
      if (resetConfirmation) {
        return false;
      } else {
        return element?.id === "navbar" || navbarElements[focusedElementIndex] === element;
      }
    };

    const isFooterFocused = (element: any) => {
      return element?.id === "footer-content" ||
         footerElements[focusedFooterElementIndex] === element;
    };

    navbarFocused = isNavbarFocused(document.activeElement);
    footerFocused = isFooterFocused(document.activeElement);

    if (navbarFocused) {
      if (focusedElementIndex === -1) {
        focusedElementIndex = 0;
      }
    }

    // Side Menu: get all li elements
    let sideMenuElements = document.querySelectorAll('#side-menu li a, #sidemenu-icons a, #sidemenu-logout, #sidemenu-language-selector > :first-child');
    if (sideBarOpen) {
      // Find the index of the currently focused element
      focusedElementIndex = Array.from(sideMenuElements).findIndex((element: Element) => 
        element === document.activeElement || element.contains(document.activeElement)
      );
      // Open the sublist when the focus is on the side menu
      showSublist = {
        tourismresearch: true,
        help: true
      };
    }

    switch (event.key) {
      case "Escape":
        if (!sideMenuFocused && sideBarOpen) {
          // Just close the dropdown, when the side menu and language dropdown are open
          sideMenuFocused = true;
          (document.getElementById("sidemenu-language-selector")?.firstChild as HTMLElement)?.focus();
        } else if (resetConfirmation) {
          // Cancel the reset confirmation
          cancelReset();
          // Update DOM
          await tick();
          // Set the focus to the reset button
          navbarFocused = true;
          (document.getElementById("reset-button") as HTMLElement)?.focus();
        } else {
          // Close the side menu
          closeSideMenu();
        }
        break;

      case "Enter":
        let focusedElement = document.activeElement as HTMLElement;
        focusedElement?.click();
        // Set the focus to the dropdown icon when the dropdown and side menu are open
        if (focusedElement?.id.includes("icon-dropdown-") && sideBarOpen) {
          sideMenuFocused = false;
        }
        if (resetConfirmation) {
          await tick();
          // Change the focus to the cancel button
          navbarFocused = false;
          (document.getElementById("reset-page-cancel") as HTMLElement)?.focus();
        }
        if (focusedElement?.id === "reset-page-cancel") {
          await tick();
          navbarFocused = true;
          (document.getElementById("reset-button") as HTMLElement)?.focus();
        }
        break;

      case "ArrowRight":
        if (navbarFocused) {
          // Focus the next element
          if (focusedElementIndex < navbarElements.length - 1) {
            ((navbarElements[focusedElementIndex + 1] as HTMLElement) as HTMLElement)?.focus();
          }
        } else if (resetConfirmation) {
          // Change the focus to the cancel button
          (document.getElementById("reset-page-cancel") as HTMLElement)?.focus();
        } else if (footerFocused) {
          // Focus the next element
          (footerElements[focusedFooterElementIndex + 1] as HTMLElement)?.focus();
        }
        break;

      case "ArrowLeft":
        if (navbarFocused) {
          // Focus the previous element
          if (focusedElementIndex > 0) {
            ((navbarElements[focusedElementIndex - 1] as HTMLElement) as HTMLElement)?.focus();
          }
        }
        else if (resetConfirmation) {
          // Change the focus to the reset button
          (document.getElementById("reset-page-confirm") as HTMLElement)?.focus();
        }
        else if (footerFocused) {
          // Focus the previous element
          (footerElements[focusedFooterElementIndex - 1] as HTMLElement)?.focus();
        }
        break;
      
      case "ArrowDown":
        if (sideMenuFocused) {
          // Focus the next element
          if (focusedElementIndex < sideMenuElements.length - 1) {
            (sideMenuElements[focusedElementIndex + 1] as HTMLElement).focus();
          }
          else {
            // If the last element is focused, focus the first element
            (sideMenuElements[0] as HTMLElement).focus();
          }
        }
        if (footerFocused) {
          // Focus the first footer element
          (footerElements[0] as HTMLElement)?.focus();
        }
        break;
      
      case "ArrowUp":
        if (sideMenuFocused) {
          // Focus the previous element
          if (focusedElementIndex > 0) {
            (sideMenuElements[focusedElementIndex - 1] as HTMLElement).focus();
          }
          else {
            // If the first element is focused, focus the last element
            (sideMenuElements[sideMenuElements.length - 1] as HTMLElement).focus();
          }
        }
        break;

      default:
        // Allow default behavior for other keys
        return;
    }

    // Prevent default behavior only for relevant keys
    event.preventDefault();
  };
  
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex a11y-click-events-have-key-events -->
{#if sideBarOpen}
<div class="gray-screen" transition:fade={{ duration: 250 }} on:click={closeSideMenu}></div>

<div id="side-menu" transition:horizontalSlide={{axis: 'x', duration: 250}} tabindex="0" on:keydown={onKeyDown}>
  <div class="close-icon-wrapper" on:click={closeSideMenu}>
    <FaTimes />
  </div>
  
  <ul class="side-list">
    <li>
      <a href="https://visitory.io/{($locale.split('-')[0])}/">{$t("Home")}</a>
    </li>
    <li>
      <a href="https://visitory.io/{($locale.split('-')[0])}/about/">{$t("About")}</a>
    </li>
    <li>
      <button id="tourismresearch" class="sublist_button" on:click|preventDefault={toggleSublist("tourismresearch")}>
        {$t("Tourism research")}
        <div class="icon-down"><FaSortDown /></div>
      </button>
      <ul class="sublist {showSublist.tourismresearch ? 'open' : ''}">
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/tourismresearch">{$t("Foreign Tourists")}</a></li>
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/tourismincome">{$t("Tourism Employment & Income")}</a></li>
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/account/travelanalytics">{$t("Travel Analytics")}</a></li>
      </ul>
    </li>
    <li>
      <a href="https://www.insights.visitory.io/{($locale.split('-')[0])}">{$t("Blog")}</a>
    </li>
    <li>
      <button id="help" class="sublist_button" on:click|preventDefault={toggleSublist("help")}>
        {$t("Help")}
        <div class="icon-down"><FaSortDown /></div>
      </button>
      <ul class="sublist {showSublist.help ? 'open' : ''}">
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/tourismresearch">{$t("FAQ")}</a></li>
        <li><a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/dictionary">{$t("Dictionary")}</a></li>
      </ul>
    </li>
    <li>
      <a href="https://visitory.io/{($locale.split('-')[0])}/contact/">{$t("Contact")}</a>
    </li>
  </ul>

  <div class="presentation-mode-side-menu">
    <ToggleSwitch bind:checked={hideVisualizationHeaders}>
      <p class="presentation-switch-text" slot="switch-text">{$t("Presentation mode")}</p>
    </ToggleSwitch>
  </div>

  {#if resetConfirmation}
    <div class="reset-container side-menu">
      <p id="reset-text">{$t("Reset page?")}</p>
      <div class="reset-wrapper" id="navbar-reset-wrapper-confirmation" tabindex="0">
        <div class="confirm" id="reset-page-confirm" tabindex="0" on:click={confirmReset}><IoMdCheckmark/></div>
        <div class="cancel" id="reset-page-cancel" tabindex="0" on:click={cancelReset}><IoMdClose/></div>
      </div>
    </div>
    {:else}
    <div class="reset-container side-menu">
      <div class="reset-wrapper" id="navbar-reset-wrapper-button">
        <button class="reset-button" id="reset-button" on:click={() => {resetConfirmation = true}} title="{$t("Reset page")}">{$t("Reset page")}</button>
      </div>
    </div>
    {/if}

  <div class="language-selector" id="sidemenu-language-selector">
    <IconDropdown
      options={Languages}
      handler={changeLanguageHandler}
      title="Language"
    >
      <div class="change-language" slot="icon" id="change-language">
        <div class="icon">
          <MdLanguage />
        </div>
          {Languages[$locale]}
        <div class="icon-down">
          <FaSortDown />
        </div>
      </div>
    </IconDropdown>
  </div>

  <a href="https://visitory.io/{($locale.split('-')[0])}/account/logout/" class="logout" id="sidemenu-logout">
    <div class="icon-wrapper">
      <FaSignOutAlt/>
    </div>
    {$t("Logout")}
  </a>

  <div class="side-menu-icons" id="sidemenu-icons">
    <a href="https://www.linkedin.com/showcase/visitory" class="icon"><FaLinkedin/></a>
    <a href="https://twitter.com/visitory_io/" class="icon"><FaTwitter/></a>
    <a href="https://www.facebook.com/Visitory-100600242530006/" class="icon"><FaFacebookF/></a>
  </div>

</div>
{/if}
<!-- svelte-ignore a11y-no-noninteractive-tabindex  a11y-click-events-have-key-events -->
<div id="navbar" tabindex="0" on:keydown={onKeyDown}>
  <div class="nav-left">
    <div id="navbar-menu-button">
      <button class="menu-button" on:click={openSideMenu}>
        <FaBars />
      </button>
    </div>

    <div class="nav-logo" id="navbar-nav-logo">
      <a href="https://visitory.io/{($locale.split('-')[0])}">
          <img alt="Visitory" src={visitoryBlackLogoUrl}/>
      </a>
    </div>

    <div class="presentation-mode-header">
      <ToggleSwitch bind:checked={hideVisualizationHeaders}>
        <p class="presentation-switch-text" slot="switch-text">{$t("Presentation mode")}</p>
      </ToggleSwitch>
    </div>
  </div>

  <div class="nav-right">
    {#if resetConfirmation}
    <div class="reset-container">
      <p id="reset-text">{$t("Reset page?")}</p>
      <div class="reset-wrapper" id="navbar-reset-wrapper-confirmation" tabindex="0">
        <div class="confirm" id="reset-page-confirm" tabindex="0" on:click={confirmReset}><IoMdCheckmark/></div>
        <div class="cancel" id="reset-page-cancel" tabindex="0" on:click={cancelReset}><IoMdClose/></div>
      </div>
    </div>
    {:else}
    <div class="reset-container">
      <div class="reset-wrapper" id="navbar-reset-wrapper-button">
        <button class="reset-button" id="reset-button" on:click={() => {resetConfirmation = true}} title="{$t("Reset page")}">{$t("Reset page")}</button>
      </div>
    </div>
    {/if}
    
    <div class="view-selector" id="navbar-view-selector">
      <ViewsDropdown
        on:loadView={loadView}
      >
        <div class="view-list" slot="icon" id="view-list">
          <div class="icon">
            <FaDatabase />
          </div>
            {$t("Views")}
          <div class="icon-down">
            <FaSortDown />
          </div>
        </div>
      </ViewsDropdown>
    </div>

    <div class="language-selector" id="navbar-language-selector">
      <IconDropdown
        options={Languages}
        handler={changeLanguageHandler}
        title="Language"
      >
        <div class="change-language" slot="icon" id="change-language">
          <div class="icon">
            <MdLanguage />
          </div>
            {Languages[$locale]}
          <div class="icon-down">
            <FaSortDown />
          </div>
        </div>
      </IconDropdown>
    </div>
    <div tabindex="0" id="app-logout">
      <a href="https://visitory.io/{($locale.split('-')[0])}/account/logout/" class="logout">
        <div class="logout-button">
          <div class="icon-wrapper">
            <FaSignOutAlt/>
          </div>
          {$t("Logout")} 
        </div>
      </a>
    </div>
  </div>
</div>

<main>

  <div class="main-content">
    <FloatingButton on:toggleDrawer={handleToggleDrawer} visible={hideVisualizationHeaders} />
    <FeedbackDrawer dataSource={visitorySource} visible={showFeedbackDrawer} onClose={handleToggleDrawer} />

    <h1>
      <img
        alt="Visitory Dashboard"
        src={visitoryLogoUrl}
        width="300"
        height="80"
      />
    </h1>

    <!--Button only for DEV purposes-->
    <!--
    <button style="color: black; background-color: white; width: 100px; position: absolute;" on:click={getVisualisationInfo}>Save View</button>
    -->

    <div class="visualizations">
      <!-- Visualizations located here -->
    </div>

    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <div class="add-visualization-container" id="add-visualization-container" tabindex="0" on:keydown={onKeyDown}>
      <IconDropdown
        options={Visualizations}
        handler={addVisualizationHandler}
        title={$t("New visualization")}
      >
        <div class="add-visualization" slot="icon">
          <div class="icon">
            <FaPlusSquare />
          </div>
          {$t("New visualization")}
        </div>
      </IconDropdown>
    </div>
  </div>

  <footer class="footer">
    <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
    <div class="footer-content" id="footer-content" tabindex="0" on:keydown={onKeyDown}>
      <div class="footer-logo">
        <a href="https://visitory.io/{($locale.split('-')[0])}">
            <img alt="Visitory" src={visitoryLogoUrl}/>
        </a>
      </div>

      <ul class="footer-button-list">
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/">{$t("Home")}</a>
        </li>
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/about/">{$t("About")}</a>
        </li>
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/regions/">{$t("Regions")}</a>
        </li>
        <li>
          <a href="https://www.insights.visitory.io/{($locale.split('-')[0])}">{$t("Blog")}</a>
        </li>
        <li>
          <a href="https://www.insights.visitory.io/{($locale.split('-')[0])}/tourismresearch">{$t("FAQ")}</a>
        </li>
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/contact/">{$t("Contact")}</a>
        </li>
        <li>
          <a href="https://visitory.io/{($locale.split('-')[0])}/privacy/">{$t("Privacy policy")}</a>
        </li>
      </ul>

      <div class="footer-icons" id="footer-icons">
        <a href="https://www.linkedin.com/showcase/visitory" class="icon"><FaLinkedin/></a>
        <a href="https://twitter.com/visitory_io/" class="icon"><FaTwitter/></a>
        <a href="https://www.facebook.com/Visitory-100600242530006/" class="icon"><FaFacebookF/></a>
      </div>

      <h1 id="brand-text">Visitory 2019-{new Date().getFullYear()}</h1>

    </div>
  </footer>
</main>


<style lang="scss">
  @import "./media-queries";

  .gray-screen {
    position: fixed;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: rgba(68, 68, 68, 0.5);
    z-index: 9000;
  }

  #side-menu {
    position: fixed;
    overflow-y: scroll;
    display: flex;
    flex-direction: column;
    background-color: var(--color-white);
    width: 210px;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 9999;
    padding: 30px;
    border-right: 0.5px solid var(--color-black);
    align-items: center;
    

    .close-icon-wrapper {
      position: absolute;
      width: 17px;
      color: var(--color-gray);
      display: flex;
      justify-content: flex-end;
      left: 1.9rem; 
      cursor: pointer;

      transition: ease-in-out 0.1s;
      &:hover{
        color: var(--color-black);
      }
    }

    .side-list {
      padding: 0;
      text-transform: uppercase;
      list-style-type: none;
      text-align: center;
      margin-bottom: 3rem;

      .sublist_button {
        display: inline-flex;
        align-items: center;
        justify-content: space-around;
        padding: 15px 0;
        text-transform: uppercase;
        font-weight: bold;
        cursor: pointer;
        color: black;
      }

      .sublist {
        padding: 0.25rem 1.5rem;
        font-size: 14px;
        text-transform: none;
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.5s ease-in-out;
      }

      .open {
        max-height: 15rem; /* Set to a value large enough to accommodate the content */
      }

      .icon-down{
        margin: 0px 0px 0px 10px;
        width: 10px;
      }

      a {
        display: block;
        color: var(--color-black);
        padding: 15px 0;
        text-decoration: none;
        font-weight: 700;
      }
    }

    .logout {
      position: relative;
      display: flex;
      align-items: center;
      text-align: center;
      padding: 0.5rem 1rem;
      color: var(--color-black);
      font-weight: bold;
      text-decoration: none;
      justify-content: center;
      cursor: pointer;

      .icon-wrapper {
        margin-right: 0.5rem;
        width: 15px;
        height: 20px;
      }
    }

    .presentation-mode-side-menu {
      margin-bottom: 15px;
    }

    @media screen and (min-width: 768px) {
      .presentation-mode-side-menu {
        display: none;
      }
    }

    .presentation-switch-text {
      color: black;
      font-size: 0.95rem;
      font-weight: 600;
      white-space: nowrap;
    }

    .language-selector {
      //background-color: var(--color-white);
      //border: 2px solid var(--color-primary);
      border-radius: 0.4rem;
      position: relative;
      top: 0;
      right: 0;
      margin-bottom: 20px;

      .change-language {
        color: var(--color-black);
        display: flex;
        flex-direction: row;
        padding: 5px 10px 5px 10px;
        align-items: center;
        font-weight: bold;

        .icon {
          margin: 0px 10px 0px 0px;
        }

        .icon-down{
          margin: 0px 0px 0px 10px;
          width: 10px;
        }
      }
      :global(ul) {
          // use parent's width
          margin: 0 !important;
          right: -1px;

          color: var(--color-black);
          background-color: var(--color-white);
        }
        // When hover change color and background-color with ease
        transition: color 0.2s ease, background-color 0.2s ease;
        &:hover {
          background-color: var(--color-primary); 
          #change-language {
            color: var(--color-white);
          }
        }
    }

    .side-menu-icons {
      display: flex;
      margin-top: 50px;
      gap: 40px;

      .icon {
        width: 22px;
        height: 22px;
        color: var(--color-black);
        cursor: pointer;
        transition: ease-in-out 0.1s;
        
        &:hover{
          color: var(--color-gray);
        }
      }
    }
  }
  
  #navbar {
    position: fixed;
    display: flex;
    justify-content: space-between;
    background-color: var(--color-white);
    height: 70px;
    width: 100vw;
    z-index: 999;

    .nav-left, .nav-right {
      display: flex;
      flex-direction: row;
      align-items: center;
      width: wrap;
    }

    .nav-left {
      justify-content: flex-start;
    }

    .nav-right {
      justify-content: flex-end;
      gap: 1.5rem;
      padding-right: 1rem;

      @media screen and (max-width: 768px) {
        padding-right: 0;
      }
    }

    @media screen and (max-width: 768px) {
      .presentation-mode-header {
        display: none;
      }
      }

    .presentation-switch-text {
      color: black;
      font-size: 1.05rem;
      font-weight: 600;
    }

    .menu-button {  
      background-color: transparent;
      border: none;
      color: var(--color-black);
      cursor: pointer;
      width: 20px;
      height: 30px;
      margin: 5px 0 0 30px;
    }

    @media screen and (max-width: 768px) {
      .menu-button {
        display: flex;
        justify-content: flex-start;
      }
    }

    .nav-logo {
      position: relative;
      height: 60px;
      width: 150px;
      display: flex;
      align-items: center;
      margin-left: 2rem;

      a {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
    }

    @media screen and (max-width: 768px) {
      .nav-logo {
        height: 35px;
        width: 130px;
        justify-content: flex-start;
        margin-left: 1.5rem;
      }
    }

    .logout {
      display: flex;
      align-items: center;
      text-align: center;
      padding: 0.5rem 1rem;
      color: var(--color-black);
      font-weight: bold;
      text-decoration: none;
      cursor: pointer;

      @media screen and (max-width: 768px) {
        display: none;
      }

      .icon-wrapper {
        margin-right: 0.5rem;
        width: 15px;
        height: 20px;
      }

      .logout-button {
        display: flex;
        align-items: center;
        cursor: pointer;
      }
    }
    .language-selector {
      background-color: var(--color-white);
      border-radius: 0.4rem;
      right: 12rem;
      top: 1.1rem;

      @media screen and (max-width: 768px) {
        display: none;
      }

      .change-language {
        color: var(--color-black);
        display: flex;
        flex-direction: row;
        padding: 5px 10px 5px 10px;
        align-items: center;
        font-weight: bold;

        .icon {
          margin: 0px 10px 0px 0px;
        }

        .icon-down{
          margin: 0px 0px 0px 10px;
          width: 10px;
        }
      }
      :global(ul) {
          // use parent's width
          margin: 0 !important;
          right: -1px;

          color: var(--color-black);
          background-color: var(--color-white);
        }
        // When hover change color and background-color with ease
        transition: color 0.2s ease, background-color 0.2s ease;
        &:hover {
          background-color: var(--color-primary); 
          #change-language {
            color: var(--color-white);
          }
        }
    }

    .view-selector {
      background-color: var(--color-white);
      border-radius: 0.4rem;


      .view-list {
        color: var(--color-black);
        display: flex;
        flex-direction: row;
        padding: 5px 10px 5px 10px;
        align-items: center;
        font-weight: bold;

        .icon {
          margin: 0px 10px 0px 0px;
        }

        .icon-down{
          margin: 0px 0px 0px 10px;
          width: 10px;
        }
      }
      :global(ul) {
          // use parent's width
          margin: 0 !important;
          right: -1px;

          color: var(--color-black);
          background-color: var(--color-white);
        }
        // When hover change color and background-color with ease
        transition: color 0.2s ease, background-color 0.2s ease;
        &:hover {
          background-color: var(--color-primary); 
          #view-list {
            color: var(--color-white);
          }
        }
    }
  }

  main {
    width: 100vw;
    padding: 0;
    margin: 0;
    
    display: flex;
    flex-direction: column;
    align-items: center;
    

    @include size(tablet) {
      padding-left: 0;
      padding-right: 0;
    }

    .main-content{
      width: var(--app-width, 1200px);
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      margin-top: 70px; // Compensate for the navbar
      padding-bottom: 8rem; // leave space for the "add visualization" button
      row-gap: 4rem;

      .visualizations {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        row-gap: 2.813rem;
        column-gap: 0.9615%;
        flex-direction: row;
      }

      .add-visualization-container {
        display: flex;
        justify-content: center;

        .add-visualization {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          padding: 0.75rem 1.5rem;
          line-height: 1;

          background-color: var(--color-primary);
          border-radius: 0.5rem;

          user-select: none;
        }
        :global(ul) {
          // use parent's width
          margin: 0;
          left: 0;
          right: 0;

          color: var(--color-white);
          background-color: var(--color-black);
        }
      }
    }

    h1 {
      flex-basis: 100%;
      text-align: center;
    }
  }

  .reset-container {
    display: flex;
    flex-direction: row;
    width: auto;
    height: auto;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    @media screen and (max-width: 975px) { // none standard width, but needed for the reset button
      margin-right: 1rem;
      display: none;
    }

    #reset-text {
      color: var(--color-black);

      @media screen and (max-width: 768px) {
        display: none;
      }
    }
    
    &.side-menu {
      display: none;
      @media screen and (max-width: 975px) {
        display: block;
        margin: 0;
        margin-bottom: 20px;
      }
    }

    .reset-wrapper {
      background-color: var(--color-white);
      border: 2px solid var(--color-primary);
      border-radius: 0.4rem;
      width: auto;
      height: 35px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      @media screen and (max-width: 768px) {
        justify-content: flex-start;
        width: auto;
        height: auto;
      }


      .reset-button {
        cursor: pointer;
        color: var(--color-black);
        width: auto;
        min-width: 101px;
        height: 35px;
        padding: 0 0.5rem;

        // When hover change color and background-color with ease
        transition: color 0.2s ease, background-color 0.2s ease;
        &:hover {
          background-color: var(--color-primary);
          color: var(--color-white);
        }
        @media screen and (max-width: 768px) {
          font-size: 14px;
        }
      }

      .confirm {
        cursor: pointer;
        background-color: var(--color-white);
        color: var(--color-primary);
        width: 35px;
        height: 35px;
        margin-left: 0.5rem;
        margin-right: 0.5rem;

      }
      .cancel {
        cursor: pointer;
        background-color: var(--color-white);
        color: var(--color-tertiary);
        width: 35px;
        height: 35px;
        margin-right: 0.5rem;
      }

      @media screen and (max-width: 768px) {
        .cancel,
        .confirm {
          width: 35px;
          height: 35px;
        }
      }

    }
  }

  .footer {
    position: relative;
    background-color: #232124;
    border-top: solid 1px #232124;
    text-align: center;
    width: 100%;
    bottom: 0;
    left: 0;

    .footer-content {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 40px 0px 40px 0px;
      margin: 0 auto 0 auto;
      align-items: center;

      .footer-logo {
        position: relative;
        width: 150px;
        margin-bottom: 20px;
      }

      .footer-button-list {
        display: flex;
        flex-direction: row;
        gap: 25px;

        text-transform: uppercase;
        list-style-type: none;

        a {
          display: block;
          color: var(--color-white);
          text-decoration: none;
          font-weight: 700;
        }

        @media screen and (max-width: 768px) {
          flex-wrap: wrap;
          justify-content: center;
          gap: 2vw;
          padding: 0 10vw;
        }
      }
      .footer-icons {
        display: flex;
        margin-top: 30px;
        gap: 40px;

        .icon {
          width: 22px;
          height: 22px;
          color: var(--color-white);
          cursor: pointer;
          transition: ease-in-out 0.1s;
          &:hover{
            color: var(--color-gray);
          }
        }
      }

      #brand-text {
        margin-top: 30px;
        font-size: 0.9rem;
        font-weight: bold;
        color: var(--color-white);
      }
      
    }
  }
</style>
